import { createContext, useContext } from 'react';
import { GuestRedeemResponse } from './api-types';

/**
 * Where the magic-link redeem landed. Drives the guest page: `loading` → spinner,
 * `active` → the CV builder (resume), everything else → a terminal/error screen.
 */
export type GuestRedeemState =
  | 'loading'
  | 'active'
  | 'expired' // grant past its window — request a new link
  | 'submitted' // CV already submitted — terminal
  | 'invalid' // a link token was present but rejected by the server
  | 'missing-token' // no token in the URL (e.g. a mid-session refresh) — reopen the link
  | 'unavailable'; // feature flag off (404)

export interface GuestSession {
  /** In-memory bearer session token; null until redeemed (never persisted). */
  sessionToken: string | null;
  /** Redeem outcome. */
  redeemState: GuestRedeemState;
  /** Redeem payload (CV + acknowledged steps + progress) for the data provider to seed. */
  redeemData: GuestRedeemResponse | null;
  /**
   * Mark the CV as submitted — called after a successful `POST /submit`. Flips
   * `redeemState` to `submitted` so the page shows the terminal confirmation screen.
   */
  markSubmitted: () => void;
  /**
   * The freshest session token, bypassing React state. For multi-step async flows
   * (flush → recover → submit): a recovery that resolved inside the same continuation has
   * already rotated the token, but the closed-over `sessionToken` state is still one
   * render behind. `sessionToken` is for rendering; this is for writes.
   */
  getSessionToken: () => string | null;
  /**
   * Re-redeem the link token for a fresh session WITHOUT resetting the form — called when a
   * guest write returns 401 mid-edit while the link itself is still valid (the 60-min session
   * lapsed, but the 7-day grant has not). Refreshes the live session token and returns it so
   * the failed write can retry; returns null when the grant is genuinely dead, in which case
   * `redeemState` has already been flipped to the matching terminal screen. Single-flight.
   */
  recoverSession: () => Promise<string | null>;
  /**
   * Route a write that hit a dead grant (submitted elsewhere, window over, revoked) to the
   * matching terminal screen: reports the outcome, drops the cached session so a remount
   * cannot resurrect it, and flips `redeemState`. `stage` labels the Datadog signal.
   */
  routeGrantError: (error: unknown, stage: 'autosave' | 'submit') => void;
}

export const GuestSessionContext = createContext<GuestSession | undefined>(
  undefined
);

export const useGuestSession = (): GuestSession => {
  const context = useContext(GuestSessionContext);
  if (!context) {
    throw new Error(
      'useGuestSession must be used within a GuestSessionProvider'
    );
  }
  return context;
};
